import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../Elements/Select/Select";
import MainInput from "../../Elements/MainInput/MainInput";
import copyIcon from "../../../assets/icons/common/copy-alt.png";

import { fetchQrCodeSvg, fetchInstallationInstructions } from "../../../services/EsimService";
import "./InstallationGuide.css";

const InstallationGuide = ({ selectedEsim, deviceType, setDeviceType }) => {
  const { t, i18n } = useTranslation();
  const [installMethod, setInstallMethod] = useState("qr");
  const [qrSvg, setQrSvg] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [loadingInstructions, setLoadingInstructions] = useState(false);

  const [installationSteps, setInstallationSteps] = useState([]);
  const [activationSteps, setActivationSteps] = useState([]);

  const installMethodOptions = useMemo(
    () => [
      { value: "qr", label: t("esim.installationGuide.qrCode", "QR code") },
      { value: "manual", label: t("esim.installationGuide.manual", "Manual") },
    ],
    [t]
  );

  const deviceTypeOptions = useMemo(
    () => [
      { value: "IOS", label: t("esim.installationGuide.devices.ios", "iPhone (iOS)") },
      { value: "android", label: t("esim.installationGuide.devices.android", "Android") },
      { value: "samsung", label: t("esim.installationGuide.devices.samsung", "Samsung Galaxy") },
      { value: "pixel", label: t("esim.installationGuide.devices.pixel", "Google Pixel") },
      { value: "xiaomi", label: t("esim.installationGuide.devices.xiaomi", "Xiaomi") },
    ],
    [t]
  );

  const parseActivationCode = (code) => {
    if (!code) return { smdp: "", activation: "", lpa: "" };
    const parts = code.split("$");
    return {
      smdp: parts[1] || "",
      activation: parts[2] || "",
      lpa: code,
    };
  };

  const { smdp, activation, lpa } = parseActivationCode(selectedEsim?.activation_code);

  const getDevicePrefix = (deviceType) => {
    return deviceType.toUpperCase() === "IOS" ? "IOS" : deviceType.toLowerCase();
  };

  useEffect(() => {
    if (deviceType && installMethod) {
      setLoadingInstructions(true);
      fetchInstallationInstructions(deviceType, installMethod, i18n.language)
        .then((data) => {
          const typePrefix = getDevicePrefix(deviceType);
          const methodSuffix = installMethod === "qr" ? "Qr" : "Manual";

          const installationKey = `${typePrefix}${methodSuffix}InstallationSteps`;
          const activationKey = `${typePrefix}${methodSuffix}ActivationSteps`;

          setInstallationSteps(data?.[installationKey] || []);
          setActivationSteps(data?.[activationKey] || []);
        })
        .finally(() => setLoadingInstructions(false));
    }
  }, [deviceType, installMethod, i18n.language]);

  useEffect(() => {
    if (installMethod === "qr" && selectedEsim?.iccid) {
      fetchQr(selectedEsim.iccid);
    } else {
      setQrSvg(null);
    }
  }, [installMethod, selectedEsim]);

  const renderStep = (step) => {
    return step.replace(/\*(.*?)\*/g, '<span style="font-weight:600">$1</span>');
  };

  const fetchQr = async (iccid) => {
    setLoadingQr(true);
    try {
      const svg = await fetchQrCodeSvg(iccid);
      let cleaned = svg
        .replace(/svg:/g, "")
        .replace(/xmlns:svg="[^"]*"/g, "")
        .replace(/fill_color="[^"]*"/g, "")
        .replace(/back_color="[^"]*"/g, "")
        .replace(/width="37mm"/, 'width="37"')
        .replace(/height="37mm"/, 'height="37"')
        .replace(/([="\s])([0-9.]+)mm([\s">])/g, "$1$2$3");

      if (!/viewBox=/.test(cleaned)) {
        cleaned = cleaned.replace(/<svg([^>]*)>/, '<svg$1 viewBox="0 0 37 37">');
      }
      setQrSvg(cleaned.trim());
    } catch (error) {
      console.error("QR ERROR:", error);
    } finally {
      setLoadingQr(false);
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="installation-guide">
      <div className="installation-selects-row">
        <Select
          label={t("esim.installationGuide.methodLabel", "Method")}
          value={installMethod}
          onChange={setInstallMethod}
          options={installMethodOptions}
        />
        <Select
          label={t("esim.installationGuide.deviceTypeLabel", "Device type")}
          value={deviceType}
          onChange={setDeviceType}
          options={deviceTypeOptions}
        />
      </div>

      <div className="installation-main">
        <div className="content-left">
          {installMethod === "qr" ? (
            <div className="qr-container">
              {loadingQr ? (
                <div className="loading-qr">
                  {t("esim.installationGuide.generatingQr", "Generating QR code...")}
                </div>
              ) : qrSvg ? (
                <>
                  <div className="qr-svg" dangerouslySetInnerHTML={{ __html: qrSvg }} />
                  <div className="qr-description">
                    <span>
                      {t(
                        "esim.installationGuide.scanQrDesc",
                        "Scan this QR code to install the eSIM. Ensure a stable internet connection."
                      )}
                      <span style={{ fontWeight: 600 }}>
                        {t("esim.installationGuide.enableRoaming", " Please remember to enable roaming.")}
                      </span>
                    </span>
                  </div>
                </>
              ) : (
                <div className="error-qr">{t("esim.installationGuide.failedQr", "Failed to load QR code")}</div>
              )}
            </div>
          ) : (
            <div className="manual-container">
              <div className="manual-field">
                <label>{t("esim.installationGuide.smdpLabel", "SM-DP+ Address")}</label>
                <div className="input-with-copy">
                  <MainInput value={smdp} readOnly placeholder="rsp-eu.simlessly.com" />
                  <button
                    className={`copy-btn ${copiedField === "smdp" ? "copied" : ""}`}
                    onClick={() => copyToClipboard(smdp, "smdp")}
                    title={t("esim.installationGuide.copyToClipboard", "Copy to clipboard")}
                  >
                    <img src={copyIcon} alt="Copy" />
                  </button>
                </div>
              </div>

              <div className="manual-field">
                <label>{t("esim.installationGuide.activationCodeLabel", "Activation Code")}</label>
                <div className="input-with-copy">
                  <MainInput value={activation} readOnly placeholder="5A11EE53772346F0ABE57D914A883743" />
                  <button
                    className={`copy-btn ${copiedField === "activation" ? "copied" : ""}`}
                    onClick={() => copyToClipboard(activation, "activation")}
                    title={t("esim.installationGuide.copyToClipboard", "Copy to clipboard")}
                  >
                    <img src={copyIcon} alt="Copy" />
                  </button>
                </div>
              </div>

              <div className="manual-field">
                <label>{t("esim.installationGuide.lpaLabel", "Full LPA Code")}</label>
                <div className="input-with-copy">
                  <MainInput value={lpa} readOnly placeholder="LPA:1$rsp-eu.simlessly.com$..." />
                  <button
                    className={`copy-btn ${copiedField === "lpa" ? "copied" : ""}`}
                    onClick={() => copyToClipboard(lpa, "lpa")}
                    title={t("esim.installationGuide.copyToClipboard", "Copy to clipboard")}
                  >
                    <img src={copyIcon} alt="Copy" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="content-right">
          <div className="instructions-panel">
            {loadingInstructions ? (
              <div className="loading-instructions">
                {t("esim.installationGuide.loadingInstructions", "Loading instructions...")}
              </div>
            ) : (
              <>
                {/* INSTALACJA */}
                {installationSteps.length > 0 && (
                  <div className="instruction-section">
                    <h5>{t("esim.installationGuide.installationHeading", "Installation")}</h5>
                    <ol className="instruction-steps">
                      {installationSteps.map((step, index) => (
                        <li key={`install-${index}`} dangerouslySetInnerHTML={{ __html: renderStep(step) }} />
                      ))}
                    </ol>
                  </div>
                )}

                {/* AKTYWACJA */}
                {activationSteps.length > 0 && (
                  <div className="instruction-section">
                    <h5>{t("esim.installationGuide.activationHeading", "Activation")}</h5>
                    <ol className="instruction-steps">
                      {activationSteps.map((step, index) => (
                        <li key={`activate-${index}`} dangerouslySetInnerHTML={{ __html: renderStep(step) }} />
                      ))}
                    </ol>
                  </div>
                )}

                {!installationSteps.length && !activationSteps.length && (
                  <div className="no-instructions">
                    {t("esim.installationGuide.noInstructions", "No instructions available")}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallationGuide;
